import { createRef, useState } from 'react'

import { Location } from '@types'

import { AsyncTypeahead, Menu, MenuItem } from 'react-bootstrap-typeahead'
import Typeahead from 'react-bootstrap-typeahead/types/core/Typeahead'

import useLocale from '@hooks/client/useLocale'
import useHttpRequest from '@hooks/useHttpRequest'
import getDictionary from '@lib/helpers/cross-env/get-dictionary'

import styles from './typeahead.module.scss'

export type LocationTypeAheadProps = {
  currentLocation: Location
  setLocation: (location: Location) => void
  setShowError: (value: boolean) => void
  showError: boolean
  placeholder?: string
  maxResults?: number
  className?: string
}

export default function LocationTypeAhead({
  currentLocation,
  setLocation,
  setShowError,
  showError,
  placeholder,
  maxResults,
  className,
}: LocationTypeAheadProps): JSX.Element {
  const { get } = useHttpRequest()
  const locale = useLocale()
  const { zipCode } = getDictionary(locale)
  const actualPlaceholder = placeholder || `City or ${zipCode}`

  const [locations, setLocations] = useState<Location[]>([])
  const [isInputEmpty, setIsInputEmpty] = useState<boolean>(true)

  const locationRef = createRef<Typeahead>()

  const handleLocationSearch = async (query: string): Promise<void> => {
    const items: Location[] | undefined = await get<Location[]>(
      `locations/autocomplete?query=${encodeURIComponent(query)}`,
      { service: 'web' },
    )
    if (items) {
      setLocations(items)
    }
  }

  return (
    <div className={`${showError ? styles['typeahead-error'] : ''} ${styles.typeahead} ${className || ''}`}>
      <AsyncTypeahead
        id="location-search"
        isLoading={false}
        labelKey="displayName"
        minLength={3}
        maxResults={maxResults}
        paginate={false}
        onSearch={handleLocationSearch}
        inputProps={{ 'aria-label': 'Location search input' }}
        onChange={(selected): void => {
          if (selected && selected.length > 0) {
            const location = selected[0] as Location
            setShowError(false)
            
            // Format displayName to ensure state is uppercase
            let formattedDisplayName = location.displayName
            let formattedState = location.state
            
            // Uppercase the state abbreviation
            if (location.state) {
              formattedState = location.state.toUpperCase()
              
              // If displayName contains a comma, uppercase the state part
              if (formattedDisplayName.includes(',')) {
                const parts = formattedDisplayName.split(',')
                if (parts.length >= 2) {
                  formattedDisplayName = parts.slice(0, -1).join(',') + ', ' + formattedState
                }
              } else if (formattedDisplayName.toLowerCase().endsWith(location.state.toLowerCase())) {
                // If state is at the end without comma, uppercase it
                const stateIndex = formattedDisplayName.toLowerCase().lastIndexOf(location.state.toLowerCase())
                if (stateIndex !== -1) {
                  formattedDisplayName = 
                    formattedDisplayName.substring(0, stateIndex) + 
                    formattedState
                }
              }
            }
            
            setLocation({
              name: location.name,
              id: location.id,
              type: location.type,
              location: location.location?.map((p) => p.toString()),
              displayName: formattedDisplayName,
              metropolis: location.metropolis,
              county: location.county,
              state: formattedState,
              postcode: location.postcode,
              active: location.active,
            })
          }
        }}
        onInputChange={(): void => {
          const inputValue = locationRef?.current?.getInput()?.value
          setIsInputEmpty(!locationRef?.current?.getInput()?.value?.length)
          if (!inputValue) {
            setLocation({
              name: '',
              id: '',
              type: '',
              location: [],
              displayName: '',
              metropolis: '',
              county: '',
              state: '',
              postcode: '',
              active: true,
            })
          }
        }}
        filterBy={(): boolean => {
          return true
        }}
        onFocus={(): void => setShowError(false)}
        ref={locationRef}
        options={locations}
        data-testid="location-typeahead"
        defaultSelected={currentLocation ? [currentLocation] : []}
        emptyLabel={<div className={styles['item-no-match']}>No results found</div>}
        searchText={<div className={styles['item-searching']}>Searching</div>}
        promptText={<div className={styles['item-prompt']}></div>}
        placeholder={showError ? "This field can't be empty" : actualPlaceholder}
        renderMenu={(results, menuProps, state): JSX.Element => (
          <Menu className={styles['typeahead-item-menu']} {...menuProps} maxHeight="260px">
            {results.length > 0 && locationRef?.current?.getInput()?.value.replace(/ /g, '') !== '' && (
              <div className={styles['menu-title']}>Select a location</div>
            )}
            {results.map((result, index) => {
              const location = result as Location
              // Format displayName to ensure state abbreviation is uppercase
              let formattedDisplayName = location.displayName
              
              // If displayName contains a comma, likely has state - uppercase the state part
              if (location.state && formattedDisplayName.includes(',')) {
                const parts = formattedDisplayName.split(',')
                if (parts.length >= 2) {
                  // Uppercase the state abbreviation (last part after comma)
                  const statePart = parts[parts.length - 1].trim().toUpperCase()
                  formattedDisplayName = parts.slice(0, -1).join(',') + ', ' + statePart
                }
              } else if (location.state) {
                // Check if state appears in displayName and uppercase it
                const stateLower = location.state.toLowerCase()
                const displayLower = formattedDisplayName.toLowerCase()
                const stateIndex = displayLower.indexOf(stateLower)
                if (stateIndex !== -1) {
                  formattedDisplayName = 
                    formattedDisplayName.substring(0, stateIndex) + 
                    location.state.toUpperCase() +
                    formattedDisplayName.substring(stateIndex + location.state.length)
                }
              }
              
              // Bold the matched text while preserving the formatted displayName
              const searchText = state.text.toLowerCase()
              const displayLower = formattedDisplayName.toLowerCase()
              const searchIndex = displayLower.indexOf(searchText)
              
              return (
                <MenuItem option={result} position={index} key={index}>
                  <div
                    className={
                      styles['typeahead-item'] +
                      (state.activeIndex === index ? ` ${styles['typeahead-item-active']}` : '')
                    }
                  >
                    {searchIndex !== -1 ? (
                      <>
                        {formattedDisplayName.substring(0, searchIndex)}
                        <b>{formattedDisplayName.substring(searchIndex, searchIndex + state.text.length)}</b>
                        {formattedDisplayName.substring(searchIndex + state.text.length)}
                      </>
                    ) : (
                      formattedDisplayName
                    )}
                  </div>
                </MenuItem>
              )
            })}
          </Menu>
        )}
      />
      {showError && !isInputEmpty ? <div className={styles.error}>Select a location</div> : null}
    </div>
  )
}
